import React, { useState, useCallback } from 'react';
import type { WorkloadIndicatorType } from '../../stores/workloadStore';

// ─── Demo data per mode ──────────────────────────────────────────────────────

interface DayData {
  label: string;
  isWeekend?: boolean;
  isToday?: boolean;
  bars: Array<{ height: number; type: 'worklog' | 'remaining' | 'scheduled' | 'over' | 'ghost' | 'items' | 'empty'; value?: string }>;
  displayValue?: string;
}

interface DemoConfig {
  days: DayData[];
  capacityHeight?: number; // % height for the capacity line (relative to max)
  capacityLabel?: string;
  annotation?: string;
  legend: Array<{ color: string; label: string }>;
}

const MAX_BAR_HEIGHT = 100; // px

function getDemoConfig(mode: WorkloadIndicatorType): DemoConfig {
  switch (mode) {
    case 'worklog_workload':
      return {
        capacityHeight: 80, // 8h = 80% of max (10h)
        capacityLabel: '8h capacity',
        days: [
          { label: 'Mon', bars: [{ height: 60, type: 'worklog', value: '6h' }], displayValue: '6h' },
          { label: 'Tue', bars: [{ height: 80, type: 'worklog', value: '8h' }], displayValue: '8h' },
          { label: 'Wed', bars: [{ height: 40, type: 'worklog', value: '4h' }], displayValue: '4h' },
          { label: 'Thu', isToday: true, bars: [{ height: 30, type: 'worklog', value: '3h' }, { height: 50, type: 'remaining', value: '5h' }], displayValue: '3+5h' },
          { label: 'Fri', bars: [{ height: 60, type: 'remaining', value: '6h' }], displayValue: '6h' },
          { label: 'Sat', isWeekend: true, bars: [], displayValue: '--' },
          { label: 'Sun', isWeekend: true, bars: [], displayValue: '--' },
        ],
        annotation: 'Past = actual worklogs | Today = logged + remaining | Future = remaining estimate. Worklogs appear as subtle gray events on the scheduler.',
        legend: [
          { color: '#66ccff', label: 'Actual worklogs' },
          { color: '#00c875', label: 'Remaining estimate' },
          { color: '#676879', label: 'Worklog event' },
        ],
      };

    case 'remaining_estimate':
      return {
        capacityHeight: 80,
        capacityLabel: '8h capacity',
        days: [
          { label: 'Mon', bars: [{ height: 2, type: 'empty' }], displayValue: '\u2013' },
          { label: 'Tue', bars: [{ height: 2, type: 'empty' }], displayValue: '\u2013' },
          { label: 'Wed', bars: [{ height: 2, type: 'empty' }], displayValue: '\u2013' },
          { label: 'Thu', isToday: true, bars: [{ height: 40, type: 'remaining', value: '4h' }], displayValue: '4h' },
          { label: 'Fri', bars: [{ height: 40, type: 'remaining', value: '4h' }], displayValue: '4h' },
          { label: 'Sat', isWeekend: true, bars: [], displayValue: '--' },
          { label: 'Sun', isWeekend: true, bars: [], displayValue: '--' },
        ],
        annotation: 'Remaining = Original Estimate \u2212 Worklogs. Example: 10h \u2212 6h = 4h remaining, split across today + future',
        legend: [
          { color: '#d0d4e4', label: 'Past (not shown)' },
          { color: '#00c875', label: 'Remaining hours' },
        ],
      };

    case 'availability':
      return {
        capacityHeight: 80,
        capacityLabel: '8h capacity',
        days: [
          { label: 'Mon', bars: [{ height: 60, type: 'ghost' }, { height: 20, type: 'remaining', value: '2h' }], displayValue: '2h' },
          { label: 'Tue', bars: [{ height: 80, type: 'ghost' }], displayValue: '0h' },
          { label: 'Wed', bars: [{ height: 30, type: 'ghost' }, { height: 50, type: 'remaining', value: '5h' }], displayValue: '5h' },
          { label: 'Thu', isToday: true, bars: [{ height: 100, type: 'over', value: '-2h' }], displayValue: '-2h' },
          { label: 'Fri', bars: [{ height: 80, type: 'remaining', value: '8h' }], displayValue: '8h' },
          { label: 'Sat', isWeekend: true, bars: [], displayValue: '--' },
          { label: 'Sun', isWeekend: true, bars: [], displayValue: '--' },
        ],
        annotation: 'Free hours = capacity \u2212 scheduled work',
        legend: [
          { color: '#e6e9ef', label: 'Scheduled' },
          { color: '#00c875', label: 'Free hours' },
          { color: '#e2445c', label: 'Over capacity' },
        ],
      };

    case 'workload':
      return {
        capacityHeight: 80,
        capacityLabel: '8h capacity',
        days: [
          { label: 'Mon', bars: [{ height: 40, type: 'scheduled', value: '4h' }], displayValue: '4h' },
          { label: 'Tue', bars: [{ height: 80, type: 'scheduled', value: '8h' }], displayValue: '8h' },
          { label: 'Wed', bars: [{ height: 60, type: 'scheduled', value: '6h' }], displayValue: '6h' },
          { label: 'Thu', isToday: true, bars: [{ height: 100, type: 'over', value: '12h' }], displayValue: '12h' },
          { label: 'Fri', bars: [{ height: 20, type: 'scheduled', value: '2h' }], displayValue: '2h' },
          { label: 'Sat', isWeekend: true, bars: [], displayValue: '--' },
          { label: 'Sun', isWeekend: true, bars: [], displayValue: '--' },
        ],
        annotation: 'Pure estimate-based workload \u2014 worklogs not considered',
        legend: [
          { color: '#00c875', label: 'Estimated hours' },
          { color: '#e2445c', label: 'Over capacity' },
        ],
      };

    case 'issues':
      return {
        days: [
          { label: 'Mon', bars: [{ height: 40, type: 'items', value: '2' }], displayValue: '2' },
          { label: 'Tue', bars: [{ height: 60, type: 'items', value: '3' }], displayValue: '3' },
          { label: 'Wed', bars: [{ height: 20, type: 'items', value: '1' }], displayValue: '1' },
          { label: 'Thu', isToday: true, bars: [{ height: 100, type: 'items', value: '5' }], displayValue: '5' },
          { label: 'Fri', bars: [], displayValue: '0' },
          { label: 'Sat', isWeekend: true, bars: [], displayValue: '--' },
          { label: 'Sun', isWeekend: true, bars: [], displayValue: '--' },
        ],
        annotation: 'Counts tasks per person per day \u2014 no hours involved',
        legend: [
          { color: '#0073ea', label: 'Number of tasks' },
        ],
      };
  }
}

// ─── Component ───────────────────────────────────────────────────────────────

interface MiniTimelineDemoProps {
  mode: WorkloadIndicatorType;
}

const MiniTimelineDemo: React.FC<MiniTimelineDemoProps> = ({ mode }) => {
  const [animKey, setAnimKey] = useState(0);
  const config = getDemoConfig(mode);

  const replay = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setAnimKey((k) => k + 1);
  }, []);

  const capacityTop = config.capacityHeight
    ? MAX_BAR_HEIGHT - config.capacityHeight
    : undefined;

  return (
    <div className="rp-mode-demo" data-animate="true" key={animKey}>
      <button className="rp-mode-demo__replay" onClick={replay} title="Replay animation">
        &#x21bb;
      </button>

      <div className="rp-mode-demo__grid" style={{ height: MAX_BAR_HEIGHT }}>
        {/* Capacity line */}
        {capacityTop !== undefined && (
          <>
            <div className="rp-mode-demo__capacity-line" style={{ top: capacityTop }} />
            <span className="rp-mode-demo__capacity-label" style={{ top: capacityTop }}>
              {config.capacityLabel}
            </span>
          </>
        )}

        {config.days.map((day, i) => (
          <div
            key={i}
            className={`rp-mode-demo__day${day.isWeekend ? ' rp-mode-demo__day--weekend' : ''}${day.isToday ? ' rp-mode-demo__day--today' : ''}`}
          >
            {day.bars.map((bar, j) => (
              <div
                key={j}
                className={`rp-mode-demo__bar rp-mode-demo__bar--${bar.type}`}
                style={{ height: bar.height }}
              >
                {bar.value && <span className="rp-mode-demo__value">{bar.value}</span>}
              </div>
            ))}
            {day.bars.length === 0 && day.displayValue && (
              <span className="rp-mode-demo__value rp-mode-demo__value--dash">{day.displayValue}</span>
            )}
          </div>
        ))}
      </div>

      {/* Day labels */}
      <div className="rp-mode-demo__grid" style={{ height: 'auto', alignItems: 'center' }}>
        {config.days.map((day, i) => (
          <div key={i} className="rp-mode-demo__day" style={{ height: 'auto' }}>
            <span className="rp-mode-demo__day-label">{day.label}</span>
          </div>
        ))}
      </div>

      {/* Annotation */}
      {config.annotation && (
        <div className="rp-mode-demo__annotation">{config.annotation}</div>
      )}

      {/* Legend */}
      <div className="rp-mode-demo__legend">
        {config.legend.map((item, i) => (
          <span key={i} className="rp-mode-demo__legend-item">
            <span className="rp-mode-demo__legend-swatch" style={{ background: item.color }} />
            {item.label}
          </span>
        ))}
      </div>
    </div>
  );
};

export default MiniTimelineDemo;
